import {
  ChatWidgetData,
  submitChatWidgetAnswer,
  useChatWidgetText,
} from "../../Helper/ChatWidget";
import { BagCard } from "./Cards/BagCard";
import { BookingCard } from "./Cards/BookingCard";
import { ConfirmCard } from "./Cards/ConfirmCard";
import { FeatureRequestsCard } from "./Cards/FeatureRequestsCard";
import { FormCard } from "./Cards/FormCard";
import { InvoicesCard } from "./Cards/InvoicesCard";
import { OfferCard } from "./Cards/OfferCard";
import { OrderCard } from "./Cards/OrderCard";
import { PaymentIssueCard } from "./Cards/PaymentIssueCard";
import { PlanCompareCard } from "./Cards/PlanCompareCard";
import { ProductsCard } from "./Cards/ProductsCard";
import { QuickRepliesCard } from "./Cards/QuickRepliesCard";
import { ReturnCard } from "./Cards/ReturnCard";
import { ShowMeCard } from "./Cards/ShowMeCard";
import { StatusCard } from "./Cards/StatusCard";
import { SubscriptionCard } from "./Cards/SubscriptionCard";
import { useRef } from "react";
import { ChatWidgetAnswer, cx } from "./ChatWidgetPrimitives";
import { useSteadyCard } from "./SteadyCard";

interface ChatWidgetProps {
  /** The Kai comment the card belongs to. */
  commentId: string;
  widget: ChatWidgetData;
}

const renderCard = (
  widget: ChatWidgetData,
  onAnswer: ChatWidgetAnswer,
  text: ReturnType<typeof useChatWidgetText>,
) => {
  switch (widget.type) {
    case "quick_replies":
      return <QuickRepliesCard widget={widget} onAnswer={onAnswer} />;
    case "show_me":
      return <ShowMeCard widget={widget} text={text} />;
    case "form":
      return <FormCard widget={widget} onAnswer={onAnswer} text={text} />;
    case "confirm":
      return <ConfirmCard widget={widget} onAnswer={onAnswer} text={text} />;
    case "status":
      return <StatusCard widget={widget} />;
    case "booking":
      return <BookingCard widget={widget} onAnswer={onAnswer} text={text} />;
    case "subscription":
      return <SubscriptionCard widget={widget} text={text} />;
    case "invoices":
      return <InvoicesCard widget={widget} text={text} />;
    case "payment_issue":
      return (
        <PaymentIssueCard widget={widget} onAnswer={onAnswer} text={text} />
      );
    case "plan_compare":
      return (
        <PlanCompareCard widget={widget} onAnswer={onAnswer} text={text} />
      );
    case "products":
      return <ProductsCard widget={widget} onAnswer={onAnswer} text={text} />;
    case "order":
      return <OrderCard widget={widget} text={text} />;
    case "return":
      return <ReturnCard widget={widget} onAnswer={onAnswer} text={text} />;
    case "bag":
      return <BagCard widget={widget} text={text} />;
    case "offer":
      return <OfferCard widget={widget} text={text} />;
    case "feature_requests":
      return (
        <FeatureRequestsCard widget={widget} onAnswer={onAnswer} text={text} />
      );
    default:
      return null;
  }
};

/**
 * One of Kai's cards under its reply (SPEC §2, 15 types). Display cards only
 * show; the interactive ones send the customer's answer back as their message
 * and then fold into a receipt (or keep the pick marked: products, plan
 * compare; answered quick replies disappear). An answered card takes no second answer, except
 * the confirm receipt's Undo and the Calendly confirmation of a booking.
 */
export const ChatWidget = ({ commentId, widget }: ChatWidgetProps) => {
  const text = useChatWidgetText();
  // A card that is still moving (scrolled to, text rendering around it) takes no tap: it would hit the wrong row.
  const cardRef = useRef<HTMLDivElement>(null);
  const holdWhileMoving = useSteadyCard(cardRef);
  const onAnswer: ChatWidgetAnswer = (action, values, label, options) => {
    const response = widget.response;
    const kind = options?.kind;
    const canAnswer =
      !response ||
      (kind === "undo" &&
        response.action === "confirm" &&
        !response.undoneAt) ||
      (kind === "followUp" &&
        widget.type === "booking" &&
        widget.booking?.status === "confirm_in_provider");
    if (!canAnswer) return;
    submitChatWidgetAnswer({
      commentId,
      widget,
      action,
      values,
      label,
      mark: kind === "undo" ? "undo" : kind === "followUp" ? "none" : "answer",
    });
  };

  // Answered quick replies leave nothing behind: the customer's message
  // right below shows the choice.
  if (widget.type === "quick_replies" && widget.response) {
    return null;
  }

  return (
    <div
      ref={cardRef}
      className={cx(
        "cw",
        `cw--${widget.type}`,
        widget.response && "cw--answered",
      )}
      data-widget-id={widget.id}
      onClickCapture={holdWhileMoving}
    >
      {renderCard(widget, onAnswer, text)}
    </div>
  );
};
